import { handleApiResult } from '@store/error';
import { apiSlice } from './apiSlice';
import { type ShareAccessLevel } from './folderEndpoints';
import { type User } from './userEndpoints';

export const teamRoles = ['MANAGER', 'INSPECTOR', 'ANALYST'] as const;

export type TeamRole = typeof teamRoles[number];

export type TeamMemberUser = Pick<User, 'id' | 'name' | 'username' | 'role'>;

export interface TeamMember {
  // Populated with basic user info by the API; null if the user was deleted.
  userId: string | TeamMemberUser | null;
  role: TeamRole;
}

export interface Team {
  _id: string;
  name: string;
  description: string;
  members: TeamMember[];
  createdAt: string;
  updatedAt: string;
}

export const memberUserId = (member: TeamMember): string | undefined =>
  typeof member.userId === 'object' ? member.userId?.id : member.userId;

export interface TeamMemberInput {
  userId: string;
  role: TeamRole;
}

export type TeamInput = Pick<Team, 'name' | 'description'> & { members: TeamMemberInput[] };

// Member changes go through the dedicated member endpoints; team updates only carry name/description.
export type UpdateTeamInput = Pick<Team, 'name' | 'description'>;

export interface TeamShareEntry {
  folder: { _id: string; name: string; path: string };
  // Counterpart team: the owner for received shares, the target for given ones.
  team: { _id: string; name: string } | null;
  accessLevel: ShareAccessLevel;
  sharedAt?: string;
}

export interface TeamShares {
  given: TeamShareEntry[];
  received: TeamShareEntry[];
}

export type TeamDirectoryEntry = Pick<Team, '_id' | 'name'>;

export const teamApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getTeams: builder.query<Team[], void>({
      query: () => '/v1/teams',
      providesTags: (result) =>
        result
          ? [
            ...result.map(({ _id }) => ({ type: 'Team' as const, id: _id })),
            { type: 'Team', id: 'LIST' },
          ]
          : [{ type: 'Team', id: 'LIST' }],
      onQueryStarted: handleApiResult(),
    }),
    addTeam: builder.mutation<void, TeamInput>({
      query: (team) => ({
        url: '/v1/teams',
        method: 'POST',
        body: team,
      }),
      // Creating a team creates its root folder, so it shows up in the folder list and at
      // the virtual root. Nothing else in the tree moves.
      invalidatesTags: [
        { type: 'Team', id: 'LIST' },
        { type: 'Folder', id: 'LIST' },
        { type: 'Folder', id: 'root' },
      ],
      onQueryStarted: handleApiResult('Api_Team_Add_Successful'),
    }),
    updateTeam: builder.mutation<void, { id: string } & UpdateTeamInput>({
      query: ({ id, ...rest }) => ({
        url: `/v1/teams/${id}`,
        method: 'PATCH',
        body: rest,
      }),
      // Renaming a team renames its root folder and rewrites the path of everything below
      // it, so every cached folder can be affected — not just the list. The list of report
      // designs names each design's team.
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Team' as const, id },
        { type: 'Team', id: 'LIST' },
        'Folder',
        { type: 'ReportDesign', id: 'LIST' },
      ],
      onQueryStarted: handleApiResult('Api_Team_Update_Successful'),
    }),
    addTeamMembers: builder.mutation<void, { id: string; members: TeamMemberInput[] }>({
      query: ({ id, members }) => ({
        url: `/v1/teams/${id}/members`,
        method: 'POST',
        body: { members },
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'Team' as const, id }, { type: 'Team', id: 'LIST' }],
      onQueryStarted: handleApiResult('Api_TeamMember_Add_Successful'),
    }),
    updateTeamMember: builder.mutation<void, { id: string; userId: string; role: TeamRole }>({
      query: ({ id, userId, role }) => ({
        url: `/v1/teams/${id}/members/${userId}`,
        method: 'PATCH',
        body: { role },
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'Team' as const, id }, { type: 'Team', id: 'LIST' }],
      onQueryStarted: handleApiResult('Api_TeamMember_Update_Successful'),
    }),
    removeTeamMember: builder.mutation<void, { id: string; userId: string }>({
      query: ({ id, userId }) => ({
        url: `/v1/teams/${id}/members/${userId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'Team' as const, id }, { type: 'Team', id: 'LIST' }],
      onQueryStarted: handleApiResult('Api_TeamMember_Delete_Successful'),
    }),
    deleteTeam: builder.mutation<void, string>({
      query: (id) => ({
        url: `/v1/teams/${id}`,
        method: 'DELETE',
      }),
      // Deleting a team detaches its root folder and clears every share granted to it and
      // every share given from under its root, so folders and shares both move. Its report
      // designs and the layers of its report brand are deleted with it.
      invalidatesTags: (_result, _error, id) => [
        { type: 'Team' as const, id },
        { type: 'Team', id: 'LIST' },
        'Folder',
        'TeamShare',
        { type: 'ReportDesign', id: 'LIST' },
        'ReportBranding',
      ],
      onQueryStarted: handleApiResult('Api_Team_Delete_Successful'),
    }),
    getTeamShares: builder.query<TeamShares, string>({
      query: (id) => `/v1/teams/${id}/shares`,
      providesTags: ['TeamShare'],
      onQueryStarted: handleApiResult(),
    }),
    getTeamDirectory: builder.query<TeamDirectoryEntry[], void>({
      query: () => '/v1/teams/directory',
      onQueryStarted: handleApiResult(),
    }),
  }),
});

export const {
  useGetTeamsQuery,
  useAddTeamMutation,
  useUpdateTeamMutation,
  useDeleteTeamMutation,
  useAddTeamMembersMutation,
  useUpdateTeamMemberMutation,
  useRemoveTeamMemberMutation,
  useGetTeamSharesQuery,
  useGetTeamDirectoryQuery,
} = teamApiSlice;
